<template>
    <Modal data-component="GroupBookingReferenceModal"
           icon="fa-hashtag"
           title="GROUP_BOOKINGS.MODAL_TITLE">
        <form class="form-v"
              @submit.prevent="ok">
            <div class="group-booking-reference-objects">
                <!-- New reference input -->
                <GroupBookingReferenceObject :is-new-reference="true"
                                             :reference-object="newReferenceObject"
                                             :selected="selectedReferenceObject === newReferenceObject"
                                             @select="selectReferenceObject"
                                             @update="updateReferenceObject"
                                             @submit="ok" />

                <!-- List of existing references from the cart to choose from or edit -->
                <GroupBookingReferenceObject v-for="(ref, index) in existingReferenceObjects"
                                             :key="index"
                                             :reference-object="ref"
                                             :selected="ref === selectedReferenceObject"
                                             @select="selectReferenceObject"
                                             @update="updateReferenceObject" />

                <!-- No reference option -->
                <GroupBookingReferenceObject v-if="allowNone"
                                             :is-no-reference="true"
                                             :reference-object="null"
                                             :selected="!selectedReferenceObject"
                                             @select="selectReferenceObject"
                                             @submit="ok" />
            </div>
        </form>
        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           data-analytics-id="Group Booking Reference / Cancel"
                           data-testid="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
                <span class="flex--grow desktop-only"></span>
                <SquareBtn :disabled="!resetButtonEnabled"
                           size="lg"
                           class="cancel-btn"
                           data-analytics-id="Group Booking Reference / Reset"
                           data-testid="btn-reset"
                           @click="reset">
                    {{ l('RESET') }}
                </SquareBtn>
                <SquareBtn class="ok-btn"
                           color="primary"
                           size="lg"
                           :disabled="!okButtonEnabled"
                           data-analytics-id="Group Booking Reference / OK"
                           data-testid="btn-ok"
                           @click="ok">
                    {{ l('GROUP_BOOKINGS.ASSIGN_GROUP_REFERENCE') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import GroupBookingReferenceObject from './GroupBookingReferenceObject';
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import { autoFocus } from '@/vue/directives/autoFocus';
import { CloseModalOnEscapeMixin } from '@/vue/mixins/CloseModalOnEscapeMixin';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

/**
 * @typedef {Object} GroupBookingReferenceObject
 * @property {string|null} originalReference - The original reference name, or null if it's a new entry.
 * @property {string} newReference - The current reference name, which can be edited.
 */

/**
 * @typedef {Object} GroupBookingReferenceModalResult
 * @property {'add' | 'rename'} type - 'add' for new or existing references,
 *                                     'rename' for an existing reference that was changed.
 * @property {string|null} [originalReference] - The original reference name if it was
 * @property {string|null} newReference - The reference name that was selected or created.
 *                                        This may be null to clear a selected reference.
 */

export default {
    components: {
        SquareBtn,
        Modal,
        GroupBookingReferenceObject,
    },

    directives: {
        autoFocus,
    },

    mixins: [
        CloseModalOnEscapeMixin,
        ModalPropsMixin,
    ],

    props: {
        initialValue: {
            type: String,
            default: null,
        },

        allowNone: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            /**
             * An object used for the new reference line.
             * This is set to a GroupBookingReferenceObject in the reset method.
             *
             * @type {?GroupBookingReferenceObject}
             */
            newReferenceObject: null,

            /**
             * An array of group booking references.
             *
             * @type {GroupBookingReferenceObject[]}
             */
            existingReferenceObjects: [],

            /**
             * The currently selected reference object from the list.
             *
             * @type {?GroupBookingReferenceObject}
             */
            selectedReferenceObject: null,
        };
    },

    computed: {
        resetButtonEnabled() {
            // Enabled if there's a selection, new reference input, or if any item has been renamed.
            const hasLocalChanges = this.existingReferenceObjects.some((refObj) => {
                return refObj.originalReference !== refObj.newReference;
            });

            return hasLocalChanges || !!this.newReferenceObject.newReference.trim();
        },

        okButtonEnabled() {
            return (this.allowNone && this.selectedReferenceObject === null)
                || (this.selectedReferenceObject && this.selectedReferenceObject.newReference.trim() !== '');
        },
    },

    created() {
        this.reset();
    },

    methods: {
        selectReferenceObject(referenceObject) {
            this.selectedReferenceObject = referenceObject;

            // When we switch to a new reference object, undo any edits made to other objects.
            this.existingReferenceObjects.forEach((refObj) => {
                if (
                    referenceObject !== refObj // Not the newly selected object
                    && refObj.originalReference // Has an original reference (so it's not the new ref row)
                    && refObj.newReference !== refObj.originalReference // The ref has been edited
                ) {
                    // Reset it to the original reference.
                    refObj.newReference = refObj.originalReference;
                }
            });
        },

        updateReferenceObject({ referenceObject, newReference }) {
            this.selectedReferenceObject = referenceObject;
            this.selectedReferenceObject.newReference = newReference;
        },

        async ok() {
            const originalReference = this.selectedReferenceObject?.originalReference || null;
            const newReference = this.selectedReferenceObject?.newReference || null;

            // Scenario 1
            // The selected item has been renamed from its original state AND its original version existed in the cart.
            // Note: new items (originalReference === null) won't trigger this confirm modal.
            if (originalReference !== null && originalReference !== newReference) {
                const existingGroupBookingReferencesInCart = this.getGroupBookingReferencesFromCart();
                const existingReferenceIsInUse = !!existingGroupBookingReferencesInCart.find(
                    (refObj) => refObj.originalReference === originalReference
                );

                // Only show confirmation if the original name actually exists in the cart.
                if (existingReferenceIsInUse) {
                    const confirmed = await container.dialogService.confirm(null, {
                        customText: [
                            container.langService.getText(
                                'GROUP_BOOKINGS.UPDATE_BOOKING_REFERENCE_MESSAGE',
                                { originalReference, newReference }
                            ),
                            container.langService.getText('GROUP_BOOKINGS.UPDATE_BOOKING_REFERENCE_MESSAGE_EXTRA'),
                        ],
                        okColor: 'primary',
                        okLabel: 'GROUP_BOOKINGS.UPDATE_BOOKING_REFERENCE_BUTTON',
                        cancelColor: 'default',
                        cancelLabel: 'CANCEL',
                    });

                    if (!confirmed) {
                        return;
                    }

                    this.resolve({
                        type: 'rename',
                        originalReference,
                        newReference,
                    });
                    return;
                }
            }

            // Scenario 2
            // Selected item is either a new item (originalReference === null) or a reference that already existed in
            // the cart and which has not been renamed i.e. (originalReference === newReference)
            this.resolve({
                type: 'add', // We use 'add' type for both new additions and selections of existing, non-renamed items
                newReference, // Always resolve the current name (newReference)
            });
        },

        /**
         * @return {{originalReference: string, newReference: string }[]}
         */
        getGroupBookingReferencesFromCart() {
            const cartLines = container.cartService.currentCart.getLines();

            // Extract all the unique group booking references from the cart lines.
            const referencesFromCart = [
                ...new Set(
                    cartLines.map((cartLine) => cartLine.groupBookingReference)
                        .filter((ref) => !!ref)
                ),
            ];

            // Sort the references alphabetically.
            referencesFromCart.sort((a, b) => a.localeCompare(b));

            return referencesFromCart.map((ref) => ({
                originalReference: ref, // The existing name on the line.
                newReference: ref, // This will update if the name is edited.
            }));
        },

        reset() {
            // Revert to the initial state when the modal was opened.
            this.newReferenceObject = {
                originalReference: null,
                newReference: '',
            };

            this.existingReferenceObjects = this.getGroupBookingReferencesFromCart();

            this.selectedReferenceObject = null;

            setTimeout(() => {
                this.selectInitialReferenceObject();
            }, 1);
        },

        selectInitialReferenceObject() {
            if (!this.initialValue && this.allowNone) {
                this.selectReferenceObject(null);
                return;
            }

            if (this.initialValue) {
                // The desired reference was given in the props.
                // If this matches an existing reference in the list, select that one.
                // Otherwise, set that as the input value.
                const existingReference = this.existingReferenceObjects.find(
                    (ref) => ref.originalReference === this.initialValue
                );
                if (existingReference) {
                    // Found an existing reference that matches the initial value. Use this one.
                    this.selectReferenceObject(existingReference);
                }
            }

            if (!this.selectedReferenceObject) {
                // If no initial reference was selected, select the new reference object.
                this.selectReferenceObject(this.newReferenceObject);
            }

            // In case an initial value was provided, and we didn't find this reference in the cart already,
            // set the value of the new reference object to the initial value.
            if (this.selectedReferenceObject.originalReference === null && this.initialValue) {
                this.selectedReferenceObject.newReference = this.initialValue;
            }
        },
    },
};
</script>

<style lang="less">
.group-booking-reference-objects {
    box-shadow: 0 3px 8px -9px black;
}
</style>
